import type { AppConfig } from "./config";

export interface PutOptions {
  /** Blob path relative to the container, e.g. "afad-2026/ankara/01J.../rgb/DJI_0001.jpg". */
  blobPath: string;
  body: Blob;
  contentType: string;
  onProgress?: (loadedBytes: number) => void;
  signal?: AbortSignal;
}

export interface UploadTransport {
  readonly mode: "proxy" | "direct";
  put(opts: PutOptions): Promise<void>;
}

/**
 * Thrown when the blob already exists.
 *
 * The SAS is create-only (sp=c), so re-uploading a file that already landed
 * returns 403 "not authorized to perform blob overwrites" rather than 409.
 * That is a success from the uploader's point of view - it means a previous
 * attempt reached Azure even though the response never made it back.
 */
export class BlobAlreadyExistsError extends Error {
  constructor() {
    super("Blob zaten mevcut.");
    this.name = "BlobAlreadyExistsError";
  }
}

export class UploadHttpError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string
  ) {
    super(`HTTP ${status}${detail ? `: ${detail.slice(0, 200)}` : ""}`);
    this.name = "UploadHttpError";
  }
}

function looksLikeOverwriteRejection(status: number, body: string): boolean {
  if (status !== 403 && status !== 409) return false;
  return /blob overwrites|BlobAlreadyExists/i.test(body);
}

/**
 * XMLHttpRequest rather than fetch: fetch exposes no upload progress events in
 * browsers, and a 12 GB session with no progress feedback is unusable.
 */
function xhrPut(url: string, opts: PutOptions, headers: Record<string, string>): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);

    xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded);

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else if (looksLikeOverwriteRejection(xhr.status, xhr.responseText || "")) {
        reject(new BlobAlreadyExistsError());
      } else {
        reject(new UploadHttpError(xhr.status, xhr.responseText || ""));
      }
    };
    xhr.onerror = () => reject(new Error("Ağ hatası."));
    xhr.ontimeout = () => reject(new Error("Zaman aşımı."));
    xhr.onabort = () => reject(new DOMException("İptal edildi.", "AbortError"));

    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort();
        return;
      }
      opts.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(opts.body);
  });
}

/** browser -> App Service -> blob. No CORS requirement; SAS never leaves the server. */
class ProxyTransport implements UploadTransport {
  readonly mode = "proxy" as const;

  put(opts: PutOptions): Promise<void> {
    const url = `/api/upload?path=${encodeURIComponent(opts.blobPath)}`;
    return xhrPut(url, opts, { "Content-Type": opts.contentType });
  }
}

/** browser -> blob. Needs CORS enabled on the storage account. */
class DirectTransport implements UploadTransport {
  readonly mode = "direct" as const;

  constructor(
    private readonly containerUrl: string,
    private readonly sasQuery: string
  ) {}

  put(opts: PutOptions): Promise<void> {
    const encoded = opts.blobPath.split("/").map(encodeURIComponent).join("/");
    const url = `${this.containerUrl}/${encoded}?${this.sasQuery}`;
    return xhrPut(url, opts, {
      "x-ms-blob-type": "BlockBlob",
      "x-ms-version": "2021-08-06",
      "Content-Type": opts.contentType,
    });
  }
}

export function createTransport(config: AppConfig): UploadTransport {
  if (config.transportMode === "direct") {
    if (!config.sasQuery) throw new Error("Direct mod için SAS bilgisi eksik.");
    return new DirectTransport(config.containerUrl, config.sasQuery);
  }
  return new ProxyTransport();
}
